import { motion } from 'framer-motion';
import { BarChart3, Gauge, Leaf, Zap } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui';
import { ProjectCard, SectionHeading } from '@/shared/components/SolarSections';
import { caseStudies, projectGallery } from '@/shared/utils/SolarData';

const projectStats = [
  { value: '150+', label: 'Completed projects', icon: Zap },
  { value: '35 MW+', label: 'Installed capacity', icon: BarChart3 },
  { value: '82k+', label: 'Trees equivalent offset', icon: Leaf },
  { value: '3.4 yrs', label: 'Best observed payback', icon: Gauge }
];

const ProjectsPage = () => {
  return (
    <div className="px-4 pt-8 pb-16 sm:px-6 lg:px-8 lg:pt-12 lg:pb-24">
      <div className="mx-auto max-w-7xl space-y-14">
        <SectionHeading
          eyebrow="Projects"
          title="Completed installations, results, and real-world value"
          description="Explore our completed projects gallery, key project statistics, and representative case studies."
          align="center"
        />

        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {projectStats.map(stat => (
            <Card key={stat.label} className="border-border-primary/70 bg-background-card/90">
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-3xl font-semibold text-text-primary">{stat.value}</p>
                    <p className="mt-2 text-sm text-text-secondary">{stat.label}</p>
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-primary/10 text-accent-primary">
                    <stat.icon className="h-5 w-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <section className="space-y-6">
          <SectionHeading
            eyebrow="Gallery"
            title="Completed projects"
            description="Representative work across residential, commercial, institutional, and industrial segments."
          />
          <div className="grid gap-6 lg:grid-cols-2">
            {projectGallery.map((project, index) => (
              <ProjectCard key={project.title} {...project} index={index} />
            ))}
          </div>
        </section>

        <section className="space-y-6">
          <SectionHeading
            eyebrow="Case studies"
            title="How we solve project challenges"
            description="A few examples of how engineering decisions translate into practical energy outcomes."
          />
          <div className="grid gap-6 lg:grid-cols-3">
            {caseStudies.map(study => (
              <motion.div
                key={study.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
              >
                <Card className="h-full border-border-primary/70 bg-background-card/90">
                  <CardHeader>
                    <CardTitle className="text-xl">{study.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 text-sm leading-7 text-text-secondary">
                    <p>{study.description}</p>
                    <div className="rounded-2xl bg-background-secondary p-4 text-text-primary">
                      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-text-tertiary">
                        Outcome
                      </p>
                      <p className="mt-2 font-medium">{study.result}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
          <Card className="border-border-primary/70 bg-background-card/90">
            <CardContent className="p-6">
              <p className="text-sm font-semibold uppercase tracking-[0.25em] text-accent-primary">
                Before and after
              </p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div className="rounded-3xl bg-background-secondary p-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-text-tertiary">
                    Before
                  </p>
                  <p className="mt-3 text-lg font-semibold text-text-primary">High grid dependency</p>
                  <p className="mt-2 text-sm leading-6 text-text-secondary">
                    Unpredictable operating costs and no visible energy strategy.
                  </p>
                </div>
                <div className="rounded-3xl bg-accent-primary/10 p-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-accent-primary">
                    After
                  </p>
                  <p className="mt-3 text-lg font-semibold text-text-primary">Solar-powered savings</p>
                  <p className="mt-2 text-sm leading-6 text-text-secondary">
                    Clean generation, monitoring, and stronger cost control across the property.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border-primary/70 bg-background-card/90">
            <CardContent className="p-6">
              <div className="aspect-[16/9] rounded-[1.5rem] bg-linear-to-br from-accent-secondary via-accent-primary to-accent-warning p-6 text-white">
                <div className="flex h-full flex-col justify-between rounded-[1.2rem] border border-white/15 bg-black/10 p-6 backdrop-blur-sm">
                  <div>
                    <p className="text-sm uppercase tracking-[0.25em] text-white/75">Project Highlights</p>
                    <p className="mt-3 text-3xl font-semibold">Reliable delivery across every segment</p>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="rounded-2xl bg-white/10 p-3 text-sm">Engineering-led design</div>
                    <div className="rounded-2xl bg-white/10 p-3 text-sm">Fast commissioning</div>
                    <div className="rounded-2xl bg-white/10 p-3 text-sm">Monitoring support</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default ProjectsPage;